/* eslint-disable react/prop-types */
import * as React from 'react';

import { Typography } from 'antd';
import { useHref, useLinkClickHandler } from 'react-router-dom';

type LinkClickHandlerParameters = Parameters<typeof useLinkClickHandler>;

type AntdRouterLinkProps = StrictOmit<
  React.ComponentPropsWithoutRef<typeof Typography.Link>,
  'href' | 'target'
> &
  Record<'to', LinkClickHandlerParameters[0]> &
  Partial<Record<'clickHandler', LinkClickHandlerParameters[1]>>;

const AntdRouterLink = React.forwardRef<
  React.ElementRef<typeof Typography.Link>,
  AntdRouterLinkProps
>(({ to, clickHandler, onClick, ...props }, ref) => {
  const { target, relative } = clickHandler ?? {};

  const href = useHref(to, { relative });
  const handleClick = useLinkClickHandler(to, clickHandler);

  return (
    <Typography.Link
      ref={ref}
      href={href}
      target={target}
      onClick={(event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
        const { defaultPrevented } = event;

        if (!defaultPrevented) {
          handleClick(event);
        }
        onClick?.(event);
      }}
      {...props}
    />
  );
});
AntdRouterLink.displayName = 'AntdRouterLink';

const AntdRouterExternalLink = React.forwardRef<
  React.ElementRef<typeof Typography.Link>,
  React.ComponentPropsWithoutRef<typeof Typography.Link>
>(
  (
    { target = '_blank', rel = 'noopener noreferrer nofollow', ...props },
    ref,
  ) => <Typography.Link ref={ref} target={target} rel={rel} {...props} />,
);
AntdRouterExternalLink.displayName = 'AntdRouterExternalLink';

export { AntdRouterExternalLink, AntdRouterLink };
